/**
 * Editor transform / texture utilities.
 *
 * Rotation convention: the editor's data model stores rotations as degrees in
 * XYZ-Euler order. Babylon's native Euler order (YXZ) differs, so we convert
 * degrees ↔ quaternion via the shared XYZ formula (web-core's
 * eulerXYZToQuaternion) and drive nodes through `rotationQuaternion`. The
 * `getSceneConfig('unity')` round-trip validates this.
 */

import { Texture, Quaternion, type Scene, type TransformNode, type Vector3 } from 'babylonjs';
import { SCENE_POSITION_LIMIT } from '@shared/constants';
// Single source of truth for the XYZ-Euler ↔ quaternion math, shared with the
// boilerplates via web-core. The editor stores rotations as degrees; web-core's
// helpers take/return radians, so these wrappers add the deg↔rad conversion.
import {
  eulerXYZToQuaternion,
  quaternionToEulerXYZ,
} from '@webcore/core/creator/creator.element';

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

const clamp1 = (v: number): number =>
  v < -SCENE_POSITION_LIMIT ? -SCENE_POSITION_LIMIT : v > SCENE_POSITION_LIMIT ? SCENE_POSITION_LIMIT : v;

/** Clamp a Vector3 in place to ±SCENE_POSITION_LIMIT on each axis (keeps elements within the grid). */
export function clampToSceneBounds(v: Vector3): Vector3 {
  v.x = clamp1(v.x);
  v.y = clamp1(v.y);
  v.z = clamp1(v.z);
  return v;
}

/** Clamp a [x,y,z] position array to ±SCENE_POSITION_LIMIT on each axis. */
export function clampPositionArray(p: number[]): number[] {
  return [clamp1(p[0]), clamp1(p[1]), clamp1(p[2])];
}

/**
 * Build a quaternion from XYZ-Euler degrees (XYZ ordering).
 * Delegates to web-core's shared `eulerXYZToQuaternion` (radians) — the single
 * source of truth so the editor, web-core viewer and boilerplates can never drift.
 */
export function quaternionFromEulerDegreesXYZ(x: number, y: number, z: number): Quaternion {
  return eulerXYZToQuaternion(x * DEG2RAD, y * DEG2RAD, z * DEG2RAD);
}

/**
 * Decompose a quaternion into XYZ-Euler degrees (inverse of the above).
 * Delegates to web-core's shared `quaternionToEulerXYZ` (radians).
 */
export function eulerDegreesXYZFromQuaternion(q: Quaternion): [number, number, number] {
  const e = quaternionToEulerXYZ(q);
  return [e.x * RAD2DEG, e.y * RAD2DEG, e.z * RAD2DEG];
}

/**
 * Loads a Babylon Texture from a base64 data URI (used for skybox/figure/floor decals).
 */
export function textureFromBase64(base64URI: string, scene: Scene): Promise<Texture> {
  return new Promise((resolve, reject) => {
    const texture = new Texture(
      base64URI,
      scene,
      false, // noMipmap
      true, // invertY — Babylon needs this for upright textures on planes
      Texture.TRILINEAR_SAMPLINGMODE,
      () => resolve(texture),
      (msg) => reject(new Error(`Failed to load texture: ${msg}`)),
    );
  });
}

/**
 * Extracts a transform payload (position/scale arrays, rotation in degrees) from a
 * Babylon node.
 */
export function getTransformElementPayload(node: TransformNode & { sceneID?: string }) {
  const rq = node.rotationQuaternion ?? Quaternion.FromEulerVector(node.rotation);
  const [rxDeg, ryDeg, rzDeg] = eulerDegreesXYZFromQuaternion(rq);
  return {
    sceneID: node.sceneID,
    position: [node.position.x, node.position.y, node.position.z],
    rotation: [rxDeg, ryDeg, rzDeg],
    scale: [node.scaling.x, node.scaling.y, node.scaling.z],
  };
}
